import { useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import { alertApiRef, useApi } from '@backstage/core-plugin-api';
import { stringifyEntityRef } from '@backstage/catalog-model';
import { useTechDocsReaderPage } from '@backstage/plugin-techdocs-react';
import {
  MAX_MESSAGE_LENGTH,
  type FeedbackSubmitRequest,
} from '@internal/plugin-techdocs-feedback-common';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { techdocsFeedbackApiRef } from '../../api/TechDocsFeedbackApi';
import { useEnvironment } from '@traton/devportal-env-kit-app';

type TechDocsFeedbackAddonContentProps = {
  useShadowRootStyles?: boolean;
};

const FeedbackAddonContent = ({
  useShadowRootStyles = true,
}: TechDocsFeedbackAddonContentProps = {}) => {
  const { entityRef, shadowRoot } = useTechDocsReaderPage();
  const { pathname, search, hash } = useLocation();
  const alertApi = useApi(alertApiRef);
  const feedbackApi = useApi(techdocsFeedbackApiRef);
  const [message, setMessage] = useState('');
  const [anonymous, setAnonymous] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const cache = useMemo(() => {
    if (!useShadowRootStyles || !shadowRoot) {
      return null;
    }

    return createCache({ key: 'techdocs-feedback', container: shadowRoot });
  }, [shadowRoot, useShadowRootStyles]);

  const handleClose = () => {
    if (!isSending) {
      setIsDialogOpen(false);
    }
  };

  const handleSubmit = async () => {
    const trimmedMessage = message.trim();
    if (!trimmedMessage || isSending) {
      return;
    }

    if (!entityRef?.name || !entityRef?.kind) {
      alertApi.post({
        message: 'Entity context is not available for this page.',
        severity: 'error',
      });
      return;
    }

    setIsSending(true);

    try {
      const origin =
        typeof window === 'undefined' ? '' : window.location.origin;
      const payload: FeedbackSubmitRequest = {
        message: trimmedMessage,
        entityRef: stringifyEntityRef(entityRef),
        page: `${origin}${pathname}${search}${hash}`,
        anonymous,
      };

      await feedbackApi.submit(payload);

      alertApi.post({ message: 'Feedback sent.', severity: 'success' });
      setMessage('');
      setIsDialogOpen(false);
    } catch (error) {
      alertApi.post({
        message: `Failed to send feedback: ${
          error instanceof Error ? error.message : String(error)
        }`,
        severity: 'error',
      });
    } finally {
      setIsSending(false);
    }
  };

  const content = (
    <>
      <Button
        variant="outlined"
        onClick={() => setIsDialogOpen(true)}
        sx={{ ml: '0.6rem' }}
      >
        GIVE FEEDBACK
      </Button>
      <Dialog
        open={isDialogOpen}
        onClose={handleClose}
        fullWidth
        maxWidth="sm"
        disablePortal={useShadowRootStyles && Boolean(shadowRoot)}
        PaperProps={{ sx: { p: 3 } }}
      >
        <DialogTitle component="div" sx={{ p: 0, mb: 3 }}>
          <Typography variant="h5" component="h2">
            Give feedback about this documentation
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <Stack spacing={0}>
            <Typography variant="body1" sx={{ mb: 3 }}>
              Please be as specific as possible.
            </Typography>
            <TextField
              label="Your message*"
              placeholder="Give feedback about this documentation"
              value={message}
              onChange={event => setMessage(event.target.value)}
              inputProps={{ maxLength: MAX_MESSAGE_LENGTH }}
              InputProps={{
                sx: {
                  alignItems: 'flex-start',
                  minHeight: 128,
                },
              }}
              multiline
              minRows={4}
              fullWidth
              sx={{ mb: 2 }}
            />
            <Stack spacing={0.5}>
              <FormControlLabel
                sx={{ alignItems: 'flex-start' }}
                control={
                  <Checkbox
                    checked={anonymous}
                    onChange={event => setAnonymous(event.target.checked)}
                  />
                }
                disableTypography
                label={
                  <Stack spacing={0} sx={{ mt: 1 }}>
                    <Typography variant="body1">Send anonymously</Typography>
                    <Typography variant="subtitle2" color="text.secondary">
                      When selected, your e-mail is removed from the feedback
                      metadata.
                    </Typography>
                  </Stack>
                }
              />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 0, pt: 3 }}>
          <Button variant="text" onClick={handleClose} disabled={isSending}>
            CANCEL
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={isSending || message.trim().length === 0}
            startIcon={
              isSending ? (
                <CircularProgress size={16} color="inherit" />
              ) : undefined
            }
          >
            SEND FEEDBACK
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );

  return cache ? (
    <CacheProvider value={cache}>{content}</CacheProvider>
  ) : (
    content
  );
};

export const TechDocsFeedbackAddonContent = (
  props: TechDocsFeedbackAddonContentProps = {},
) => {
  const { isPluginEnabled } = useEnvironment();
  if (!isPluginEnabled('techdocs-feedback')) return null;
  return <FeedbackAddonContent {...props} />;
};
